import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Loader2, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

export default function CommunityPost() {
  const { postId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forum/posts/${postId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const submitComment = async () => {
    if (!user) {
      toast.error("Please sign in to comment");
      navigate("/login");
      return;
    }
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/forum/posts/${postId}/comments`, { content: comment });
      setComment("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async () => {
    if (!confirm("Delete this post and all its comments?")) return;
    try {
      await api.delete(`/forum/posts/${postId}`);
      toast.success("Post deleted");
      navigate("/community");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteComment = async (cid) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await api.delete(`/forum/comments/${cid}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }
  if (!data) return null;

  const { post, comments } = data;
  const isAdmin = user?.role === "admin";

  return (
    <div className="container-pl py-12" data-testid="post-detail-page">
      <Link to="/community" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-amber-400 mb-8" data-testid="back-to-forum">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to forum
      </Link>

      <article className="border border-zinc-800 rounded-sm p-8 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="outline" className="rounded-sm text-xs border-amber-400/30 text-amber-400">
            {post.topic}
          </Badge>
          <span className="text-xs text-zinc-400">{new Date(post.created_at).toLocaleString()}</span>
        </div>
        <h1 className="text-3xl tracking-tight font-medium text-zinc-50 mb-2">{post.title}</h1>
        <p className="text-sm text-zinc-400 mb-6">by {post.author_name}</p>
        <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{post.content}</p>
        {isAdmin && (
          <Button
            onClick={deletePost}
            variant="outline"
            size="sm"
            className="rounded-sm mt-6 border-red-200 text-red-700 hover:bg-red-50"
            data-testid="delete-post-btn"
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} /> Delete post
          </Button>
        )}
      </article>

      <h2 className="text-xl font-medium text-zinc-50 mb-4" data-testid="comments-heading">
        {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
      </h2>

      <div className="space-y-3 mb-8">
        {comments.map((c) => (
          <div key={c.id} className="border border-zinc-800 rounded-sm p-5" data-testid={`comment-${c.id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-zinc-50">{c.author_name}</p>
              <div className="flex items-center gap-3">
                <span className="text-xs text-zinc-400">{new Date(c.created_at).toLocaleString()}</span>
                {isAdmin && (
                  <button onClick={() => deleteComment(c.id)} className="text-red-600" data-testid={`delete-comment-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{c.content}</p>
          </div>
        ))}
      </div>

      <div className="border border-zinc-800 rounded-sm p-6">
        <h3 className="text-sm font-medium text-zinc-50 mb-3">Add a comment</h3>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={user ? "Share your thoughts..." : "Sign in to comment"}
          disabled={!user}
          className="rounded-sm min-h-[100px] mb-3"
          data-testid="comment-input"
        />
        <Button
          onClick={submitComment}
          disabled={posting || !comment.trim() || !user}
          className="rounded-sm bg-blue-700 hover:bg-blue-600"
          data-testid="submit-comment-btn"
        >
          {posting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" strokeWidth={1.5} />}
          Post comment
        </Button>
      </div>
    </div>
  );
}
